import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown, { type Components } from 'react-markdown'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import atomOneDark from 'react-syntax-highlighter/dist/esm/styles/hljs/atom-one-dark'
import github from 'react-syntax-highlighter/dist/esm/styles/hljs/github'
import remarkGfm from 'remark-gfm'
import { useTheme } from '@/context/theme-provider'
import { Button } from '@/components/ui/button'
import { llmChatMarkdownProseClassName } from '../llmChatMarkdownProse'
import {
  autoLinkCelexInMarkdown,
  sanitizeCelexLinksInMarkdown,
} from './llmChatToolOutput'

function normalizeLanguageId(languageId: string): string {
  const normalized = languageId.toLowerCase()
  if (normalized === 'ts' || normalized === 'tsx') {
    return 'typescript'
  }
  if (normalized === 'js' || normalized === 'jsx') {
    return 'javascript'
  }
  if (normalized === 'sh' || normalized === 'shell') {
    return 'bash'
  }
  return normalized
}

function extractPlainText(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(extractPlainText).join('')
  }
  return String(children)
}

type CopyCodeButtonProps = {
  textToCopy: string
  copyLabel: string
  copiedLabel: string
}

function CopyCodeButton({
  textToCopy,
  copyLabel,
  copiedLabel,
}: CopyCodeButtonProps) {
  const [didCopy, setDidCopy] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setDidCopy(true)
      window.setTimeout(() => {
        setDidCopy(false)
      }, 2000)
    } catch {
      setDidCopy(false)
    }
  }, [textToCopy])

  return (
    <Button
      type='button'
      variant='secondary'
      size='icon'
      className='absolute inset-e-2 top-2 size-8 opacity-0 transition-opacity group-hover/codeblock:opacity-100'
      onClick={handleCopy}
      aria-label={didCopy ? copiedLabel : copyLabel}
      title={didCopy ? copiedLabel : copyLabel}
    >
      {didCopy ? (
        <Check className='size-3.5' aria-hidden />
      ) : (
        <Copy className='size-3.5' aria-hidden />
      )}
    </Button>
  )
}

function createMarkdownComponents(
  syntaxStyle: typeof github,
  labels: { copyCode: string; copiedCode: string },
  onOpenCitationLink?: (input: {
    href: string
    label: string
  }) => boolean | Promise<boolean>
): Components {
  return {
    a: ({ href, children, node: _node, ...props }) => {
      const label = extractPlainText(children)
      return (
        <a
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(event) => {
            if (!href || !onOpenCitationLink) {
              return
            }
            event.preventDefault()
            void Promise.resolve(onOpenCitationLink({ href, label })).then(
              (handled) => {
                if (!handled) {
                  window.open(href, '_blank', 'noopener,noreferrer')
                }
              }
            )
          }}
          {...props}
        >
          {children}
        </a>
      )
    },
    pre: ({ children }) => {
      return <div className='my-4'>{children}</div>
    },
    code: ({ className, children, node: _node, ...rest }) => {
      const languageMatch = /language-([\w-]+)/.exec(className ?? '')
      const rawText = extractPlainText(children)
      const trimmedForMatch = rawText.replace(/\n$/, '')

      if (languageMatch) {
        const languageId = normalizeLanguageId(languageMatch[1])
        return (
          <div className='group/codeblock relative overflow-hidden rounded-lg border border-border'>
            <CopyCodeButton
              textToCopy={trimmedForMatch}
              copyLabel={labels.copyCode}
              copiedLabel={labels.copiedCode}
            />
            <SyntaxHighlighter
              language={languageId}
              PreTag='div'
              style={syntaxStyle}
              customStyle={{
                margin: 0,
                borderRadius: '0.5rem',
                padding: '1rem',
                fontSize: '0.8125rem',
                lineHeight: 1.6,
              }}
            >
              {trimmedForMatch}
            </SyntaxHighlighter>
          </div>
        )
      }

      const isProbablyBlock = trimmedForMatch.includes('\n')

      if (isProbablyBlock) {
        return (
          <div className='group/codeblock relative my-4 overflow-hidden rounded-lg border border-border bg-muted/40'>
            <CopyCodeButton
              textToCopy={trimmedForMatch}
              copyLabel={labels.copyCode}
              copiedLabel={labels.copiedCode}
            />
            <pre className='overflow-x-auto p-3 pt-10 font-mono text-[0.8125rem] leading-relaxed'>
              <code>{children}</code>
            </pre>
          </div>
        )
      }

      return (
        <code
          className='rounded-md bg-muted/80 px-1.5 py-0.5 font-mono text-[0.9em] wrap-break-word'
          {...rest}
        >
          {children}
        </code>
      )
    },
  }
}

type LlmChatMarkdownContentProps = {
  text: string
  onOpenCitationLink?: (input: {
    href: string
    label: string
  }) => boolean | Promise<boolean>
}

export function LlmChatMarkdownContent({
  text,
  onOpenCitationLink,
}: LlmChatMarkdownContentProps) {
  const { resolvedTheme } = useTheme()
  const { t } = useTranslation('llmChat')

  const syntaxStyle = resolvedTheme === 'dark' ? atomOneDark : github

  const labels = useMemo(
    () => ({
      copyCode: t('copyCode'),
      copiedCode: t('copiedCode'),
    }),
    [t]
  )

  const components = useMemo(
    () => createMarkdownComponents(syntaxStyle, labels, onOpenCitationLink),
    [syntaxStyle, labels, onOpenCitationLink]
  )

  const processedText = useMemo(
    () => sanitizeCelexLinksInMarkdown(autoLinkCelexInMarkdown(text)),
    [text]
  )

  return (
    <div className={llmChatMarkdownProseClassName}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedText}
      </ReactMarkdown>
    </div>
  )
}
